import React, { useContext, useEffect, useState } from 'react';

import { Box, LinearProgress } from '@material-ui/core';
import { Redirect, Route, Switch } from 'react-router-dom';

import { DeviceContext, PayModuleProvider } from '@vyce/core/src/contexts';
import { CommonNavBarBlock } from '@vyce/core/src/components';
import { useIsMounted } from '@vyce/core/src/hooks/useIsMounted';
import {
  BACSFormat,
  HMRCSetting,
  INTERFACE_PERMISSIONS,
  PAY_PERMISSIONS,
  TabItem,
} from '@vyce/core/src/types';
import { getBACSFormatsRequest, getHMRCSettingsByCompanyRequest } from '@vyce/core/src/api/pay';
import { getPaddingForContent } from '@vyce/core/src/utils';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { useOutsourcedOnlyCompany } from '@vyce/core/src/hooks/useOutsourcedOnlyCompany';

import { PayrollDashboardModule } from 'src/modules';
import { useActions, useTypedSelector } from 'src/hooks';

import { OUTSOURCED_PAYROLL_TABS, PAYROLL_TABS } from './constants';
import { Timesheets } from './Timesheets';
import { Payruns } from './Payruns';
import { EmployerTimesheetPage } from './EmployerTimesheetPage';
import { PayrunPage } from './PayrunPage';
import { EmployerReports } from './EmployerReports';
import { EmployerPaySchedulePage } from './EmployerPaySchedulePage';
import { EmployerEmployeeList } from './EmployerEmployeeList';
import { EmployerPayrollOnboarding } from './EmployerPayrollOnboarding';
import { PaySchedules } from './components/PaySchedules';
import { GetStarted } from './components/GetStarted';

export const EmployerPayroll: React.FC = () => {
  const { selectedCompany, selectedPosition, selectedCompanyAppData } = useTypedSelector(
    state => state.helper
  );
  const [hmrcSettings, setHMRCSettings] = useState<HMRCSetting>();
  const [bacsFormats, setBacsFormats] = useState<BACSFormat[]>();
  const [tabs, setTabs] = useState<TabItem[]>(OUTSOURCED_PAYROLL_TABS);
  const [loading, setLoading] = useState<boolean>(true);
  const isMounted = useIsMounted();
  const { isMobile } = useContext(DeviceContext);
  const { fetchPaySchedules } = useActions();
  const padding = getPaddingForContent(isMobile);
  const { canAny, canAnywhere, filterTabs } = usePermissions();

  const hasPermissions = canAny([INTERFACE_PERMISSIONS.PAYROLL, INTERFACE_PERMISSIONS.PAYROLL_OUTSOURCED]);

  // Granted per pay schedule for a scoped payroll manager, so check anywhere.
  const hasPayAdminPermissions = canAnywhere(PAY_PERMISSIONS.CAN_READ_PAY_RUN);
  const outsourcedOnlyCompany = useOutsourcedOnlyCompany(selectedCompanyAppData?.paySchedules);

  const getHMRCSettings = async (companyId: string) => {
    try {
      const res = await getHMRCSettingsByCompanyRequest(companyId);
      if (!isMounted.current) return;
      setHMRCSettings(res.data);
    } catch (e) {
      throw e;
    }
  };

  const getBacsFormats = async (companyId: string) => {
    try {
      const res = await getBACSFormatsRequest(companyId);
      if (!isMounted.current) return;
      setBacsFormats(res.data);
    } catch (e) {
      throw e;
    }
  };

  const getPayrollData = async (companyId: string) => {
    try {
      await getBacsFormats(companyId);
      await getHMRCSettings(companyId);
      if (!isMounted.current) return;
      setLoading(false);
    } catch (e) {
      if (isMounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompany?.uuid && hasPermissions) {
      getPayrollData(selectedCompany.uuid);
    }
    // The outsourced set is a different product surface, not a permission
    // difference — pick the set first, then filter it by what this person holds.
    const baseTabs = hasPayAdminPermissions ? PAYROLL_TABS : OUTSOURCED_PAYROLL_TABS;
    // CIS 300 is filed by Vyce for a company with no in-house schedule, so the
    // Reports tab has nothing to show there whatever rights the person holds.
    setTabs(filterTabs(outsourcedOnlyCompany ? baseTabs.filter(tab => tab.label !== 'Reports') : baseTabs));
  }, [selectedPosition, selectedCompany, hasPayAdminPermissions, filterTabs, outsourcedOnlyCompany]);

  if (!hasPermissions) {
    return <GetStarted hasPermissions={hasPermissions} />;
  }

  if (loading || selectedCompanyAppData?.companyDataLoading) {
    return (
      <Box height="100%">
        <LinearProgress color="primary" />
      </Box>
    );
  }

  if (!selectedCompanyAppData?.paySchedules?.length) {
    return (
      <Box height="100%">
        <GetStarted />
      </Box>
    );
  }

  return (
    <Box height="100%">
      <Box>
        <CommonNavBarBlock tabItems={tabs} />

        <PayModuleProvider
          value={{
            fetchPaySchedules,
            hmrcSettings,
            bacsFormats,
          }}>
          <Box padding={padding}>
            <Switch>
              <Route path={'/payroll/create-pay-schedule'} component={EmployerPayrollOnboarding} />
              <Route path={'/payroll/dashboard'} component={PayrollDashboardModule} />
              <Route path={'/payroll/users'} component={EmployerEmployeeList} />
              <Route path={'/payroll/timesheets/:id'} component={EmployerTimesheetPage} />
              <Route path={'/payroll/timesheets'} component={Timesheets} />
              <Route path={'/payroll/payruns/:id'} component={PayrunPage} />
              <Route path={'/payroll/payruns'} component={Payruns} />
              <Route path={'/payroll/reports'} component={EmployerReports} />
              <Route path={'/payroll/settings/:schedule'} component={EmployerPaySchedulePage} />
              <Route path={'/payroll/settings'} component={PaySchedules} />
              <Route
                path="/payroll"
                render={() => {
                  return hasPayAdminPermissions ? (
                    <Redirect to={{ pathname: '/payroll/dashboard' }} />
                  ) : (
                    <Redirect to={{ pathname: '/payroll/users' }} />
                  );
                }}
              />
            </Switch>
          </Box>
        </PayModuleProvider>
      </Box>
    </Box>
  );
};
